import { useState } from "react";
import {
  Calendar as CalendarIcon,
  MoreVertical,
  Pencil,
  Plus,
  PencilIcon,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { SystemOfRecordBadge } from "../../../../components/SystemOfRecordBadge";
import { useAuth } from "../../../../contexts/authContext";
import { useDeleteWorkStepSession } from "../../../../hooks/useWorkSteps";
import { getExternalWorkStepUrl } from "../../../../lib/systemOfRecord";
import { formattedDate } from "../../../../lib/utils";
import {
  WorkStepSession,
  WorkStepWithUser,
} from "../../../../models/audit/workStep";
import { ConfiguredStateBadge } from "../../../../components/audit/ConfiguredStateBadge";
import { DeleteSessionDialog } from "../../../../components/audit/DeleteSessionDialog";

interface WorkStepDetailPanelProps {
  workStep: WorkStepWithUser;
  opsAuditName: string | null;
  sessions: WorkStepSession[];
  loadingSessions: boolean;
  onClose: () => void;
  onCreateSession: () => void;
  loadingCreateSession: boolean;
  canEditTest: boolean;
  onSessionDeleted: () => void;
}

export const WorkStepDetailPanel = ({
  workStep,
  opsAuditName,
  sessions,
  loadingSessions,
  onClose,
  onCreateSession,
  loadingCreateSession,
  canEditTest,
  onSessionDeleted,
}: WorkStepDetailPanelProps) => {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const externalWorkStepUrl =
    workspace?.systemOfRecord && workStep.externalRef
      ? getExternalWorkStepUrl(
          workspace.systemOfRecord,
          workStep.externalRef.externalOpsAuditItemId,
        )
      : null;
  const { deleteSession, loading: isDeleting } = useDeleteWorkStepSession();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] =
    useState<WorkStepSession | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, session: WorkStepSession) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    try {
      await deleteSession(sessionToDelete.id);
      toast.success("Test deleted successfully");
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
      onSessionDeleted();
    } catch (error) {
      console.error("Failed to delete work step session:", error);
      toast.error("Failed to delete test");
    }
  };

  return (
    <div className="h-screen flex flex-col border-l bg-white overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between p-6 border-b">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-medium text-gray-900 truncate">
              {workStep.name}
            </h2>
            <ConfiguredStateBadge state={workStep.configuredState} />
            {workStep.externalRef && (
              <SystemOfRecordBadge
                externalRef={workStep.externalRef}
                controlUrl={externalWorkStepUrl}
                source="auditboard"
              />
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span>Created {formattedDate(workStep.createdAt)}</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* Open Work Step Configuration */}
        <div className="space-y-4">
          <Button
            variant="outline"
            className="gap-2 w-full"
            onClick={() => navigate(`/audit/work-steps/${workStep.id}`)}
          >
            <Pencil className="h-4 w-4" />
            Edit Work Step
          </Button>
        </div>

        {/* Description */}
        {workStep.description && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Description
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed max-h-[200px] overflow-y-auto">
              <Markdown remarkPlugins={[remarkGfm]}>
                {workStep.description}
              </Markdown>
            </p>
          </div>
        )}

        {/* Details */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Audit</p>
              <p className="text-sm text-gray-900">{opsAuditName || "—"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Section</p>
              <p className="text-sm text-gray-900">{workStep.section || "—"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Subsection</p>
              <p className="text-sm text-gray-900">
                {workStep.subsection || "—"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Preparer</p>
              <p className="text-sm text-gray-900">
                {workStep.preparer || "—"}
              </p>
            </div>
            {workStep.externalRef && (
              <div className="space-y-1">
                <p className="text-xs text-gray-500">System of Record</p>
                <div className="text-sm">
                  <SystemOfRecordBadge
                    externalRef={workStep.externalRef}
                    controlUrl={externalWorkStepUrl}
                    source="auditboard"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tests */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Tests</h3>
            {/* One test per work step: created directly, named after the
                work step, and the button disappears once it exists. */}
            {!loadingSessions && sessions.length === 0 && (
              <Button
                size="sm"
                onClick={onCreateSession}
                loading={loadingCreateSession}
                disabled={!canEditTest}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                New Test
              </Button>
            )}
          </div>

          {loadingSessions ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : sessions.length > 0 ? (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(`/audit/work-step-sessions/session/${session.id}`)
                  }
                  onKeyDown={(e) => {
                    // Radix's dropdown trigger/items preventDefault but don't
                    // stopPropagation on Enter/Space, and this div-emulated
                    // button (unlike controls' <a>) would treat the bubbled
                    // keydown as card activation — only navigate when the
                    // card itself is the target.
                    if (
                      e.target === e.currentTarget &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      navigate(
                        `/audit/work-step-sessions/session/${session.id}`,
                      );
                    }
                  }}
                  className="block w-full border p-4 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {session.name}
                      </h4>
                      <Badge
                        variant={
                          session.status === "complete"
                            ? "default"
                            : session.status === "in_review"
                              ? "outline"
                              : "secondary"
                        }
                        className="flex-shrink-0"
                      >
                        {session.status === "complete"
                          ? "Complete"
                          : session.status === "in_review"
                            ? "In Review"
                            : "In Progress"}
                      </Badge>
                    </div>
                    {canEditTest && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => handleDeleteClick(e, session)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Test
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <CalendarIcon className="h-3 w-3" />
                      <span>Created at {formattedDate(session.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PencilIcon className="h-3 w-3" />
                      <span className="text-xs text-gray-500">
                        Created by {session.user?.email ?? "—"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 space-y-3">
              <div className="text-gray-400">
                <CalendarIcon className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">No tests found</p>
                <p className="text-xs text-gray-500">
                  Tests will appear here when created
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteSessionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        isDeleting={isDeleting}
        sessionName={sessionToDelete?.name}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
